import { useMemo, useState } from "react"
import { BuyToast, Panel, PurchaseLedger } from "../components/ui"
import { fmtCompact, toNum } from "../game/format"
import { useBakeryStore } from "../game/bakeryStore"
import { previewClickSound } from "../game/music"
import { COSMETICS, type CosmeticDef } from "@cookies/shared/catalog/display"

const KIND_ORDER = ["skin", "glow", "ring", "trail", "sound"] as const
const KIND_LABEL: Record<string, string> = {
  skin: "🍪 Cookie Skins",
  glow: "✨ Name Glow",
  ring: "⭕ Avatar Ring",
  trail: "☄️ Click Trail",
  sound: "🔊 Click Sounds",
}

function rowFor(def: CosmeticDef, owned: boolean, equipped: boolean, cost: string, currency: string) {
  return { def, owned, equipped, cost, currency }
}

export default function StorePage() {
  const store = useBakeryStore()
  const snap = store.snapshot
  const connected = store.connected
  const buyToast = store.buyNotice !== null && (store.buyNotice.kind === "cosmetic" || store.buyNotice.kind === "equip") ? store.buyNotice : null
  const [kind, setKind] = useState<(typeof KIND_ORDER)[number]>("skin")

  const catalog = useMemo(() => {
    const server = new Map((snap?.cosmetics ?? []).map((x) => [x.id, x]))
    return COSMETICS.map((def) => {
      const srv = server.get(def.id)
      const owned = srv ? srv.owned : (snap?.me.cosmetics?.owned ?? []).includes(def.id)
      const equipped = srv ? srv.equipped : (snap?.me.cosmetics?.equipped[def.kind] === def.id)
      return rowFor(def, owned, equipped, srv?.cost ?? String(def.cost), srv?.currency ?? def.currency)
    })
  }, [snap])

  const equipped = snap?.me.cosmetics?.equipped
  const rows = catalog.filter((r) => r.def.kind === kind)
  const ownedCount = catalog.filter((r) => r.owned).length

  return (
    <div className="cb-page">
      <Panel className="center">
        <h2 className="cb-h">✨ Cosmetic Store</h2>
        <p className="dim small" style={{ margin: 0 }}>
          Pure flex, zero gameplay effect. {ownedCount}/{catalog.length} owned
          {equipped && (equipped.skin || equipped.glow || equipped.ring || equipped.trail || equipped.sound) && (
            <span>
              {" "}· wearing: {[equipped.skin, equipped.glow, equipped.ring, equipped.trail, equipped.sound].filter((x) => x !== "").join(", ")}
            </span>
          )}
        </p>
      </Panel>

      <BuyToast notice={buyToast} />
      <PurchaseLedger history={store.purchaseHistory} kinds={["cosmetic", "equip"]} />
      <div className="cb-row" role="tablist" aria-label="Cosmetic sections">
        {KIND_ORDER.map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={kind === k}
            className={kind === k ? "cb-buy" : "cb-buy ghost"}
            onClick={() => setKind(k)}
          >
            {KIND_LABEL[k]}
          </button>
        ))}
      </div>

      {snap === null ? (
        <div className="cb-panel center small" role="status">Loading…</div>
      ) : (
        <div className="cb-stack">
          {rows.map(({ def, owned, equipped: isEq, cost, currency }) => {
            const costNum = toNum(cost)
            const have = currency === "cookies" ? store.shownCookies : store.liveCredits
            const affordable = have >= costNum
            const pendingBuy = store.isPending("cosmetic", def.id)
            const pendingEquip = store.isPending("equip", def.id) || store.isPending("equip", "none:" + def.kind)
            const pending = pendingBuy || pendingEquip
            const coin = currency === "cookies" ? "🍪" : "🪙"
            const cardClass = isEq ? "cb-card maxed" : pending ? "cb-card confirming" : owned || affordable ? "cb-card" : "cb-card muted"
            return (
              <div key={def.id} title={def.desc} className={cardClass} aria-busy={pending || undefined}>
                <span className={pending ? "cb-card-ico cookie-pulse" : "cb-card-ico"} aria-hidden="true">{def.icon}</span>
                <div className="cb-card-body">
                  <div className="cb-card-title-row">
                    <b>{def.name}{isEq ? " · Equipped" : owned ? " · Owned" : ""}</b>
                    {pending && <span className="cb-pill confirming" role="status">{pendingBuy ? "Buying…" : "Equipping…"}</span>}
                  </div>
                  <small className="cb-card-purpose" title={def.desc}>{def.desc}</small>
                  <small className={owned || affordable ? "cb-cost" : "cb-cost poor"}>
                    {owned ? (isEq ? "Worn with pride." : `Owned · paid ${coin} ${fmtCompact(cost)}`) : `Cost ${coin} ${fmtCompact(cost)}`}
                  </small>
                </div>
                {def.kind === "sound" && (
                  <button
                    type="button"
                    title={`Preview ${def.name} click sound`}
                    className="cb-buy ghost"
                    onClick={() => previewClickSound(def.id)}
                    aria-label={`Preview ${def.name} click sound`}
                  >
                    ▶
                  </button>
                )}
                {isEq ? (
                  <button
                    type="button"
                    disabled={!connected || pending}
                    title="Unequip"
                    className="cb-buy ghost"
                    onClick={() => store.equipCosmetic("none", def.kind)}
                  >
                    Unequip
                  </button>
                ) : owned ? (
                  <button
                    type="button"
                    disabled={!connected || pending}
                    title={pending ? "Equipping…" : "Equip"}
                    className={connected && !pending ? "cb-buy" : "cb-buy off"}
                    onClick={() => store.equipCosmetic(def.id)}
                  >
                    {pending ? "Equipping…" : "Equip"}
                  </button>
                ) : pendingBuy ? (
                  <button type="button" disabled aria-busy="true" title="Buying…" className="cb-buy confirming">
                    Buying…
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={!connected || !affordable}
                    title={!connected ? "Not connected — retrying…" : !affordable ? `Need ${coin} ${fmtCompact(cost)}` : `Buy for ${coin} ${fmtCompact(cost)}`}
                    className={connected && affordable ? "cb-buy" : "cb-buy off"}
                    onClick={() => store.buyCosmetic(def.id)}
                  >
                    {affordable ? "Buy" : `${fmtCompact(costNum)}`}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
